import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { feedErrorMessage, publishToFeed, unpublishFromFeed } from "../firebase/feed";
import FeedPostToasts from "../components/FeedPostToasts";

export interface ToastOrigin {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface FeedTask {
  key: number;
  debateId: string;
  kind: "post" | "remove";
  status: "working" | "done" | "failed";
  error: string | null;
  /** Where the toast flies in from, if the person started this from a control. */
  origin: ToastOrigin | null;
}

interface FeedPostContextValue {
  tasks: FeedTask[];
  /** What this session last did to each debate; newer than anything loaded earlier. */
  postedById: Record<string, boolean>;
  run: (
    kind: FeedTask["kind"],
    debateId: string,
    options?: { anonymous?: boolean; origin?: ToastOrigin | null },
  ) => Promise<boolean>;
  dismiss: (key: number) => void;
}

const FeedPostContext = createContext<FeedPostContextValue | null>(null);

export function useFeedPostContext(): FeedPostContextValue {
  const value = useContext(FeedPostContext);
  if (!value) throw new Error("useFeedPostContext must be used inside FeedPostProvider");
  return value;
}

/**
 * Runs feed posts above the routes, so a post keeps going and still reports
 * back after the person moves to another page.
 */
export function FeedPostProvider({ children }: { children: React.ReactNode }) {
  const [tasks, setTasks] = useState<FeedTask[]>([]);
  const [postedById, setPostedById] = useState<Record<string, boolean>>({});
  const nextKey = useRef(1);
  const inFlight = useRef(new Set<string>());

  const run = useCallback<FeedPostContextValue["run"]>(async (kind, debateId, options = {}) => {
    if (inFlight.current.has(debateId)) return false;
    inFlight.current.add(debateId);
    const key = nextKey.current++;
    const task: FeedTask = { key, debateId, kind, status: "working", error: null, origin: options.origin ?? null };
    const settle = (changes: Partial<FeedTask>) =>
      setTasks((list) => list.map((t) => (t.key === key ? { ...t, ...changes } : t)));
    setTasks((list) => [...list.filter((t) => t.debateId !== debateId), task]);
    try {
      if (kind === "post") await publishToFeed(debateId, options.anonymous === true);
      else await unpublishFromFeed(debateId);
      setPostedById((known) => ({ ...known, [debateId]: kind === "post" }));
      settle({ status: "done" });
      return true;
    } catch (err) {
      settle({ status: "failed", error: feedErrorMessage(err) });
      return false;
    } finally {
      inFlight.current.delete(debateId);
    }
  }, []);

  const dismiss = useCallback((key: number) => {
    setTasks((list) => list.filter((t) => t.key !== key));
  }, []);

  const value = useMemo(() => ({ tasks, postedById, run, dismiss }), [tasks, postedById, run, dismiss]);

  return (
    <FeedPostContext.Provider value={value}>
      {children}
      <FeedPostToasts tasks={tasks} onDismiss={dismiss} />
    </FeedPostContext.Provider>
  );
}
